import { useState, useEffect } from 'react';
import { Smartphone, ExternalLink, Share, Plus, X } from 'lucide-react';
import { Button } from './ui/button';

const PWAInstallPrompt = ({ onDismiss }) => {
  const [isStandalone, setIsStandalone] = useState(false);
  const [deviceType, setDeviceType] = useState('ios'); // Default to iOS for best UX
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone === true;
    setIsStandalone(standalone);

    const ua = navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(ua)) {
      setDeviceType('ios');
    } else if (/android/.test(ua)) {
      setDeviceType('android');
    } else {
      setDeviceType('desktop');
      setIsDesktop(true);
    }
    
    // Detect desktop more accurately
    const isMobileUA = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile|tablet/i.test(ua);
    const isLargeScreen = window.innerWidth > 1024;
    if (!isMobileUA && isLargeScreen) {
      setIsDesktop(true);
    }
  }, []);

  // NON mostrare su desktop - il Desktop Blocker gestirà quello
  if (isStandalone || isDesktop) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center">
      <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full sm:max-w-md p-6 shadow-2xl max-h-[90vh] overflow-y-auto animate-in slide-in-from-bottom duration-300">
        
        {/* Close button */}
        <button 
          onClick={onDismiss}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-gradient-to-br from-[#1B4332] to-[#40916C] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Smartphone className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-xl font-bold text-gray-800">
            Salva CuraCheck nella Home
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Per ricevere le notifiche in tempo reale
          </p>
          
          {/* Urgency Banner */}
          <div className="mt-3 bg-red-500 text-white text-sm font-semibold py-2 px-4 rounded-full inline-block animate-pulse">
            ⚠️ OBBLIGATORIO per ricevere notifiche
          </div>
        </div>

        {/* iOS Instructions */}
        {(deviceType === 'ios' || deviceType === 'desktop') && (
          <div className="space-y-4">
            {/* Step 1 */}
            <div className="flex items-start gap-4 p-3 bg-gray-50 rounded-xl">
              <div className="w-10 h-10 bg-[#1B4332] text-white rounded-xl flex items-center justify-center flex-shrink-0 font-bold">
                1
              </div>
              <div className="flex-1">
                <p className="font-semibold text-gray-800">Tocca "Condividi"</p>
                <p className="text-sm text-gray-500">L'icona in basso con la freccia verso l'alto</p>
                <div className="mt-2 flex items-center gap-2 text-[#1B4332]">
                  <Share className="w-5 h-5" />
                  <span className="text-sm font-medium">Cerca questa icona</span>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex items-start gap-4 p-3 bg-gray-50 rounded-xl">
              <div className="w-10 h-10 bg-[#1B4332] text-white rounded-xl flex items-center justify-center flex-shrink-0 font-bold">
                2
              </div>
              <div className="flex-1">
                <p className="font-semibold text-gray-800">Tocca "Aggiungi a Home"</p>
                <p className="text-sm text-gray-500">Scorri il menu e cerca questa opzione</p>
                <div className="mt-2 flex items-center gap-2 text-[#1B4332]">
                  <Plus className="w-5 h-5" />
                  <span className="text-sm font-medium">Aggiungi a schermata Home</span>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex items-start gap-4 p-3 bg-gray-50 rounded-xl">
              <div className="w-10 h-10 bg-[#1B4332] text-white rounded-xl flex items-center justify-center flex-shrink-0 font-bold">
                3
              </div>
              <div className="flex-1">
                <p className="font-semibold text-gray-800">Apri dalla Home e accedi</p>
                <p className="text-sm text-gray-500">Fai login una volta e resterai connesso per sempre</p>
              </div>
            </div>

            {/* Video Tutorial iOS */}
            <a 
              href="https://youtu.be/8_NWruTCkaQ"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full p-3 bg-red-50 hover:bg-red-100 rounded-xl text-red-600 font-medium transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
              Guarda il Video Tutorial iPhone
            </a>
          </div>
        )}

        {/* Android Instructions */}
        {deviceType === 'android' && (
          <div className="space-y-4">
            <div className="flex items-start gap-4 p-3 bg-gray-50 rounded-xl">
              <div className="w-10 h-10 bg-[#1B4332] text-white rounded-xl flex items-center justify-center flex-shrink-0 font-bold">
                1
              </div>
              <div className="flex-1">
                <p className="font-semibold text-gray-800">Tocca il menu ⋮</p>
                <p className="text-sm text-gray-500">I tre puntini in alto a destra</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-3 bg-gray-50 rounded-xl">
              <div className="w-10 h-10 bg-[#1B4332] text-white rounded-xl flex items-center justify-center flex-shrink-0 font-bold">
                2
              </div>
              <div className="flex-1">
                <p className="font-semibold text-gray-800">Tocca "Installa app"</p>
                <p className="text-sm text-gray-500">O "Aggiungi a schermata Home"</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-3 bg-gray-50 rounded-xl">
              <div className="w-10 h-10 bg-[#1B4332] text-white rounded-xl flex items-center justify-center flex-shrink-0 font-bold">
                3
              </div>
              <div className="flex-1">
                <p className="font-semibold text-gray-800">Apri dalla Home e accedi</p>
                <p className="text-sm text-gray-500">Fai login una volta e resterai connesso</p>
              </div>
            </div>

            <a 
              href="https://youtu.be/pcOwsaQMzQU"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full p-3 bg-red-50 hover:bg-red-100 rounded-xl text-red-600 font-medium transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
              Guarda il Video Tutorial Android
            </a>
          </div>
        )}

        {/* Bottom note */}
        <div className="mt-4 p-3 bg-red-100 border-2 border-red-400 rounded-xl">
          <p className="text-sm text-red-700 text-center font-medium">
            ⚠️ <strong>ATTENZIONE:</strong> Senza l'app installata NON riceverai le notifiche quando l'assistente somministra una terapia o registra un'attività!
          </p>
        </div>

        {/* Browser recommendation */}
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-xl">
          <p className="text-xs font-semibold text-blue-800 mb-1">📱 Browser consigliati:</p>
          <p className="text-xs text-blue-700">
            <strong>iPhone:</strong> Safari o Chrome
          </p>
          <p className="text-xs text-blue-700">
            <strong>Android:</strong> app Chrome (consigliato)
          </p>
          <p className="text-xs text-blue-600 mt-2">
            💡 Se vedi "Famiglia Non Trovata", cancella la cache/dati del browser e riprova.
          </p>
        </div>

        {/* Dismiss button */}
        <button
          onClick={onDismiss}
          className="w-full text-center text-xs text-gray-400 hover:text-gray-600 mt-4 py-2"
        >
          Continua senza notifiche (non consigliato)
        </button>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
